/**
 * CINDERHOLD — the Kiln Compact's town, built down inside a spent ember crater.
 *
 * Layout reads outward from the middle: the market square sits at the crater
 * floor, the working buildings ring it, and the terraces step up to the rim.
 * Everything is metres; the player is 1.8 m tall for scale.
 */
import { wood } from './scatter.js';
/* Half the terrain, so no wood is planted off the edge of the map. */
const HALF_MAP = [110, 110];
const CINDERHOLD_BUILDINGS = [
    // --- the four corners of the square -------------------------------------
    {
        id: 'slack-tub', name: 'The Slack Tub', landmark: 'tavern', lit: true,
        position: [-17, 13], size: [14, 11], height: 7.5, rotation: 0,
        walls: 'plaster', roof: 'gable', roofSurface: 'slate', doorFacing: 'south'
    },
    {
        id: 'forge', name: "Harrow's Forge", landmark: 'blacksmith', lit: true,
        position: [18, 12], size: [13, 12], height: 6.5,
        walls: 'darkStone', roof: 'gable', roofSurface: 'metal', doorFacing: 'south'
    },
    {
        id: 'general-store', name: 'Vale Provisions', landmark: 'store', lit: true,
        position: [-18, -13], size: [12, 10], height: 6,
        walls: 'timber', roof: 'gable', roofSurface: 'tile', doorFacing: 'north'
    },
    {
        id: 'town-hall', name: 'The Compact Hall', landmark: 'townhall', lit: true,
        position: [0, 27], size: [22, 13], height: 11,
        walls: 'stone', roof: 'hip', roofSurface: 'slate', doorFacing: 'south'
    },
    {
        id: 'training-yard-hut', name: 'Training Yard', landmark: 'training',
        position: [20, -16], size: [9, 7], height: 4.5,
        walls: 'timber', roof: 'flat', roofSurface: 'timber', doorFacing: 'west'
    },
    {
        id: 'shrine', name: 'Ember Shrine', landmark: 'shrine', lit: true,
        position: [0, -30], size: [7, 7], height: 5.5,
        walls: 'darkStone', roof: 'hip', roofSurface: 'ember', doorFacing: 'north'
    },
    // --- houses on the first terrace ----------------------------------------
    { id: 'house-1', name: 'Terrace House', position: [-38, 4], size: [8, 7], height: 5.5, rotation: 0.3, walls: 'plaster', roof: 'gable', roofSurface: 'tile', lit: true },
    { id: 'house-2', name: 'Terrace House', position: [-36, -16], size: [8, 7], height: 5, rotation: -0.2, walls: 'timber', roof: 'gable', roofSurface: 'thatch', lit: true },
    { id: 'house-3', name: 'Terrace House', position: [37, -4], size: [9, 7], height: 5.5, rotation: -0.35, walls: 'plaster', roof: 'gable', roofSurface: 'tile', lit: true },
    { id: 'house-4', name: 'Terrace House', position: [33, 26], size: [8, 8], height: 5, rotation: 0.5, walls: 'timber', roof: 'hip', roofSurface: 'slate' },
    { id: 'house-5', name: 'Terrace House', position: [-30, 30], size: [9, 7], height: 5.5, rotation: -0.6, walls: 'plaster', roof: 'gable', roofSurface: 'tile', lit: true },
    { id: 'house-6', name: 'Terrace House', position: [-12, -38], size: [8, 7], height: 5, rotation: 0.15, walls: 'timber', roof: 'gable', roofSurface: 'thatch' },
    { id: 'house-7', name: 'Terrace House', position: [14, -38], size: [8, 7], height: 5, rotation: -0.15, walls: 'plaster', roof: 'gable', roofSurface: 'tile', lit: true },
    // --- workshops on the second terrace ------------------------------------
    { id: 'kiln-1', name: 'Refinery Kiln', position: [-50, 18], size: [11, 11], height: 9, rotation: 0.4, walls: 'darkStone', roof: 'flat', roofSurface: 'metal', lit: true },
    { id: 'kiln-2', name: 'Refinery Kiln', position: [50, 20], size: [11, 11], height: 9, rotation: -0.4, walls: 'darkStone', roof: 'flat', roofSurface: 'metal', lit: true },
    { id: 'warehouse', name: 'Compact Warehouse', position: [46, -30], size: [18, 11], height: 7, rotation: -0.25, walls: 'timber', roof: 'gable', roofSurface: 'metal' },
    { id: 'gatehouse', name: 'Rim Gate', position: [0, 58], size: [14, 8], height: 9, walls: 'stone', roof: 'flat', roofSurface: 'stone', doorFacing: 'south', lit: true }
];
export const CINDERHOLD = {
    id: 'cinderhold',
    name: 'Cinderhold',
    kind: 'town',
    blurb: 'A town terraced down inside a burnt-out ember crater. The forges never go cold.',
    // On the south road looking into the square. Must stay clear of the Ember
    // Shrine at (0, -30): its wall reaches z = -26.5, and the camera sits about
    // 6 m further back again, so spawning at -26 put the camera inside it.
    spawn: [0, 0, -16],
    spawnFacing: 0,
    terrain: {
        size: [220, 220],
        surface: 'grass',
        terraces: [
            { id: 'floor', centre: [0, 0], innerRadius: 0, outerRadius: 34, height: 0, surface: 'stone' },
            { id: 'terrace-1', centre: [0, 0], innerRadius: 34, outerRadius: 48, height: 2.4, surface: 'grass' },
            { id: 'terrace-2', centre: [0, 0], innerRadius: 48, outerRadius: 62, height: 4.8, surface: 'grass' },
            { id: 'rim', centre: [0, 0], innerRadius: 62, outerRadius: 78, height: 7.6, surface: 'grass' }
        ]
    },
    ambience: {
        skyTop: '#4a7fb5',
        skyBottom: '#c3dcec',
        fogColour: '#b6ccd8',
        fogDensity: 0.0026,
        sunColour: '#fff3d6',
        sunDirection: [-0.45, -0.72, 0.52],
        ambientColour: '#7f8f9c'
    },
    buildings: CINDERHOLD_BUILDINGS,
    props: [
        { id: 'well', kind: 'well', position: [0, 0], scale: 1.2 },
        { id: 'signpost-square', kind: 'signpost', position: [4, -6] },
        // market stalls around the square
        { id: 'stall-1', kind: 'stall', position: [-8, 6], rotation: 0.2 },
        { id: 'stall-2', kind: 'stall', position: [8, 6], rotation: -0.2 },
        { id: 'stall-3', kind: 'stall', position: [-8, -6], rotation: 2.9 },
        { id: 'stall-4', kind: 'stall', position: [8, -6], rotation: 3.4 },
        { id: 'stall-5', kind: 'stall', position: [0, 9], rotation: 0 },
        { id: 'anvil-1', kind: 'anvil', position: [13, 6] },
        { id: 'brazier-1', kind: 'brazier', position: [-6, 14] },
        { id: 'brazier-2', kind: 'brazier', position: [6, 14] },
        { id: 'brazier-3', kind: 'brazier', position: [0, -22] },
        { id: 'brazier-4', kind: 'brazier', position: [0, 50] },
        { id: 'lamp-1', kind: 'lamp', position: [-14, 0] },
        { id: 'lamp-2', kind: 'lamp', position: [14, 0] },
        { id: 'lamp-3', kind: 'lamp', position: [0, 18] },
        // Beside the spawn, not on it: [0, -16] is where players arrive, and a lamp
        // post through the middle of the camera is a poor first impression.
        { id: 'lamp-4', kind: 'lamp', position: [4.5, -17] },
        { id: 'lamp-5', kind: 'lamp', position: [-26, 20] },
        { id: 'lamp-6', kind: 'lamp', position: [26, 20] },
        { id: 'cart-1', kind: 'cart', position: [-22, 4], rotation: 1.2 },
        { id: 'cart-2', kind: 'cart', position: [24, -8], rotation: -0.6 },
        { id: 'crate-1', kind: 'crate', position: [-24, 8] }, { id: 'crate-2', kind: 'crate', position: [-25.5, 9.2], scale: 0.8 },
        { id: 'crate-3', kind: 'crate', position: [26, 4] }, { id: 'crate-4', kind: 'crate', position: [27.4, 5.1], scale: 0.85 },
        { id: 'crate-5', kind: 'crate', position: [42, -26] }, { id: 'crate-6', kind: 'crate', position: [43.6, -24.8], scale: 0.9 },
        { id: 'barrel-1', kind: 'barrel', position: [-11, 8] }, { id: 'barrel-2', kind: 'barrel', position: [-12.2, 8.9] },
        { id: 'barrel-3', kind: 'barrel', position: [12, 7] },
        { id: 'barrel-4', kind: 'barrel', position: [-22, -9] },
        { id: 'rock-1', kind: 'rock', position: [-58, -10], scale: 2.2 },
        { id: 'rock-2', kind: 'rock', position: [60, 4], scale: 1.8 },
        { id: 'rock-3', kind: 'rock', position: [-46, -40], scale: 2.6 },
        { id: 'rock-4', kind: 'rock', position: [52, 44], scale: 2 },
        { id: 'rock-5', kind: 'rock', position: [-64, 34], scale: 1.6 },
        { id: 'rock-6', kind: 'rock', position: [30, -56], scale: 2.4 },
        { id: 'tree-1', kind: 'tree', position: [-44, 46], scale: 0.9 },
        { id: 'tree-2', kind: 'tree', position: [-52, 40], scale: 0.75 },
        { id: 'tree-3', kind: 'tree', position: [56, -46], scale: 0.8 },
        // --- BrainO network signs along the south approach road ---------------
        { id: 'sign-goldtrack', kind: 'sitesign', position: [-9, -12], rotation: 0, label: 'GoldTrack' },
        { id: 'sign-podium32', kind: 'sitesign', position: [9, -12], rotation: 0, label: 'Podium 32' },
        { id: 'sign-jayandjack', kind: 'sitesign', position: [-9, -20], rotation: 0, label: 'Jay & Jack' },
        { id: 'sign-tinytalkers', kind: 'sitesign', position: [9, -20], rotation: 0, label: 'Tiny Talkers' },
        { id: 'sign-brainoaway', kind: 'sitesign', position: [-9, -28], rotation: 0, label: 'BrainO Away' },
        { id: 'sign-planawed', kind: 'sitesign', position: [9, -28], rotation: 0, label: 'Plan A Wedding' },
        { id: 'sign-builder', kind: 'sitesign', position: [-9, -36], rotation: 0, label: 'Braino Builder' },
        { id: 'sign-brainoaid', kind: 'sitesign', position: [9, -36], rotation: 0, label: 'BrainO Aid' },
        { id: 'fence-1', kind: 'fence', position: [16, -22], rotation: 0 },
        { id: 'fence-2', kind: 'fence', position: [24, -22], rotation: 0 },
        { id: 'fence-3', kind: 'fence', position: [28, -18], rotation: 1.5708 },
        /* The rim woods.
         *
         * Kept outside the crater — everything here is beyond the outermost
         * terrace, so nothing is planted in the market square or across a road.
         * The mix leans dead and burnt on the ember side of the map and turns to
         * living pine on the cold side, which is the quickest way to say that the
         * ember came from one direction.
         */
        ...wood('ch-north', {
            centre: [-6, 72], innerRadius: 12, outerRadius: 40, count: 40, seed: 3301,
            avoid: CINDERHOLD_BUILDINGS, bounds: HALF_MAP,
            mix: ['pine', 'pine', 'pine', 'tree', 'tree', 'bush', 'bush', 'stump'],
            scale: 0.95
        }),
        ...wood('ch-west', {
            centre: [-74, 6], innerRadius: 8, outerRadius: 32, count: 30, seed: 1789,
            avoid: CINDERHOLD_BUILDINGS, bounds: HALF_MAP,
            mix: ['pine', 'tree', 'bush', 'bush', 'deadtree', 'stump'],
            scale: 0.9
        }),
        // Downwind of the kilns: burnt out, mostly stumps and standing dead wood.
        ...wood('ch-south', {
            centre: [4, -74], innerRadius: 14, outerRadius: 38, count: 34, seed: 6553,
            avoid: CINDERHOLD_BUILDINGS, bounds: HALF_MAP,
            mix: ['deadtree', 'deadtree', 'deadtree', 'stump', 'stump', 'bush', 'tree'],
            scale: 0.85
        }),
        ...wood('ch-east', {
            centre: [76, 20], innerRadius: 8, outerRadius: 30, count: 28, seed: 2048,
            avoid: CINDERHOLD_BUILDINGS, bounds: HALF_MAP,
            mix: ['deadtree', 'stump', 'bush', 'tree', 'pine'],
            scale: 0.9
        })
    ],
    roads: [
        { id: 'road-north', points: [[0, 12], [0, 22], [0, 40], [0, 56]], width: 7, surface: 'road' },
        { id: 'road-south', points: [[0, -10], [0, -24], [0, -44], [0, -62]], width: 7, surface: 'road' },
        { id: 'road-east', points: [[10, 0], [26, 0], [44, -6], [62, -10]], width: 6, surface: 'road' },
        { id: 'road-west', points: [[-10, 0], [-26, 2], [-44, 8], [-62, 12]], width: 6, surface: 'road' },
        { id: 'square-ring', points: [[-12, -8], [12, -8], [12, 8], [-12, 8], [-12, -8]], width: 4, surface: 'stone' }
    ],
    water: [],
    adSlots: [
        {
            id: 'CINDERHOLD-SQUARE-BILLBOARD-01', label: 'Market square billboard',
            position: [0, 4.6, 11.5], rotation: Math.PI, size: [7.2, 4],
            format: 'billboard', showOnMobile: true, showOnDesktop: true, viewDistance: 55
        },
        {
            id: 'CINDERHOLD-TAVERN-POSTER-01', label: 'Slack Tub wall poster',
            position: [-10.2, 3.2, 13], rotation: Math.PI / 2, size: [2.4, 3.4],
            format: 'poster', showOnMobile: false, showOnDesktop: true, viewDistance: 28
        },
        {
            id: 'CINDERHOLD-FORGE-SIGN-01', label: 'Forge shop sign',
            position: [18, 4.1, 5.6], rotation: 0, size: [3.6, 1.4],
            format: 'shopSign', showOnMobile: true, showOnDesktop: true, viewDistance: 34
        },
        {
            id: 'CINDERHOLD-STALL-SPONSOR-01', label: 'Sponsored market stall',
            position: [0, 2.7, 9], rotation: 0, size: [3, 1.2],
            format: 'stall', showOnMobile: true, showOnDesktop: true, viewDistance: 26
        },
        {
            id: 'CINDERHOLD-GATE-BANNER-01', label: 'Rim gate banner',
            position: [0, 5.4, 54.2], rotation: Math.PI, size: [6, 2.6],
            format: 'banner', showOnMobile: true, showOnDesktop: true, viewDistance: 48
        }
    ],
    bounds: { min: [-78, -78], max: [78, 78] },
    // Only exits to places that actually exist. An exit to a zone that has not
    // been built is a "coming soon" sign standing in the world, which is the one
    // thing this build is not allowed to have.
    exits: [
        { id: 'to-saltmarch', toZone: 'saltmarch', position: [0, -66], radius: 5, label: 'Saltmarch' }
    ]
};
//# sourceMappingURL=cinderhold.js.map